import { 
  Button,
  Typography, 
  InputNumber, 
  Card, 
  Switch, 
  Select,
  Upload,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { audioMessage as message } from '../../Common/Notifications/audioMessage';
import { isStaffEmail } from "@utils/user_utils";
import { ColorPickerWithLabel } from './ColorPickerWithLabel';
import { ACWRThresholdConfig } from './ACWRThresholdConfig';
import React from "react";
import { useState } from "react";
import "../styles.css";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import {
  updateTeamSettings,
  setModifiedSettings,
  setSettingsResponse
} from "../../../store/actions/settings.actions"
import { getTeamInfo } from "../../../store/actions/team.actions";
import { 
  PermissionedButton, 
  PermissionedInputNumber, 
  PermissionedSelect,
  PermissionedSwitch,
  useUserPermissions
} from "../../PermissionedComponents";

const { Text } = Typography;

const acwrCalculationMethodOptions = [
  { value: "rolling_average", label: "Rolling Average" },
  { value: "ewma", label: "EWMA" },
];

export const OverviewSettings = () => {
  const dispatch = useDispatch();
  const [sample1TextColor, setSample1TextColor] = useState("black");
  const [sample1BGColor, setSample1BGColor] = useState("white");
  const [sample2TextColor, setSample2TextColor] = useState("black");
  const [sample2BGColor, setSample2BGColor] = useState("white");
  const [hideTopSpeedThreshold, setHideTopSpeedThreshold] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const { user } = useAuth0();
  const isStaff = isStaffEmail(user?.email);

  const {
    info: teamInfo,
    metrics
  } = useSelector(({ team }) => team);
  const userPermissions = useUserPermissions();

  // Check if user has permission to edit general team settings
  const hasEditGeneralTeamSettingsPermission = userPermissions?.EditGeneralTeamSettings;

  const {
    loading: { teamSettings: loading },
    teamSettingsResponse,
    modifiedSettings: {
      teamColors,
      homeTimeZone,
      automaticOutlierApproval,
      playersAppPermission,
      preferredDateFormat,
      acwrAcuteWindow,
      acwrChronicWindow,
      acwrCalculationMethod,
      ewmaAcuteTimeConstant,
      ewmaChronicTimeConstant,
      topSpeedPercThreshold,
      chronicIncludesAcute
    },
    error
  } = useSelector(({ settings }) => settings);

  const isEwma = acwrCalculationMethod === "ewma";

  useEffect(()=> {
    dispatch(getTeamInfo());
    dispatch(setSettingsResponse(null));
  }, []);

  useEffect(()=> {
    if (metrics !== undefined && metrics.length > 0) {
      const topSpeedExists = metrics.some(metric => metric.key == "top_speed");
      setHideTopSpeedThreshold(!topSpeedExists);
    }
  }, [metrics]);

  useEffect(()=> {
    if (teamSettingsResponse !== null) {
      if (teamSettingsResponse) {
        setTeamSettings(teamSettingsResponse);
        message.success("Team Settings Updated Successfully");
      } else {
        message.error("Error Occurred While Updating Team Settings. Please Try Again Later.");
      }
      dispatch(setSettingsResponse(null));
    }
  }, [teamSettingsResponse]);

  const setTeamSettings = (teamInfo) => {
    if(teamInfo !== undefined && Object.keys(teamInfo).length > 0) {
      let defaultSettings = {};
      defaultSettings["gender"] = teamInfo.gender;
      defaultSettings["teamColors"] = teamInfo.team_colors || teamInfo.colors;
      defaultSettings["homeTimeZone"] = teamInfo.home_timezone;
      defaultSettings["automaticOutlierApproval"] = !teamInfo.auto_outlier_approval;
      defaultSettings["playersAppPermission"] = teamInfo.players_view_data_permission
      defaultSettings["preferredDateFormat"] = teamInfo.date_format;
      defaultSettings["topSpeedPercThreshold"] = teamInfo.top_speed_threshold;
      defaultSettings["acwrAcuteWindow"] = teamInfo.acwr_acute_window;
      defaultSettings["acwrChronicWindow"] = teamInfo.acwr_chronic_window;
      defaultSettings["chronicIncludesAcute"] = teamInfo.chronic_includes_acute;
      defaultSettings["acwrCalculationMethod"] = teamInfo.acwr_calculation_method ?? "rolling_average";
      defaultSettings["ewmaAcuteTimeConstant"] = teamInfo.ewma_acute_time_constant;
      defaultSettings["ewmaChronicTimeConstant"] = teamInfo.ewma_chronic_time_constant;

      if (teamInfo?.team_colors !== undefined && Object.keys(teamInfo?.team_colors).length > 0) {
        if (Object.keys(teamInfo?.team_colors).includes("all")) {
          setSample1BGColor(teamInfo?.team_colors?.all[0]);
          setSample2BGColor(teamInfo?.team_colors?.all[1]);
        }
        
        if (Object.keys(teamInfo?.team_colors).includes("font_colors")) {
          setSample1TextColor(teamInfo?.team_colors?.font_colors[0]);
          setSample2TextColor(teamInfo?.team_colors?.font_colors[1]);
        }
      }
      
      dispatch(setModifiedSettings(defaultSettings));
    }
  }

  useEffect(() => {
    setTeamSettings(teamInfo)
  }, [teamInfo])


  const timezoneOptions = [
    {
      key: 1,
      value: 'US/Alaska',
      label: 'US/Alaska',
    },
    {
      key: 2,
      value: 'US/Hawaii',
      label: 'US/Hawaii',
    },
    {
      key: 3,
      value: 'US/Pacific',
      label: 'US/Pacific',
    },
    {
      key: 4,
      value: 'US/Mountain',
      label: 'US/Mountain',
    },
    {
      key: 5,
      value: 'US/Central',
      label: 'US/Central',
    },
    {
      key: 6,
      value: 'US/Eastern',
      label: 'US/Eastern',
    },
    {
      key: 7,
      value: 'America/Anchorage',
      label: 'America/Anchorage',
    },
    {
      key: 8,
      value: 'America/Los_Angeles',
      label: 'America/Los_Angeles',
    },
    {
      key: 9,
      value: 'America/Denver',
      label: 'America/Denver',
    },
    {
      key: 10,
      value: 'America/Chicago',
      label: 'America/Chicago',
    },
    {
      key: 11,
      value: 'America/New_York',
      label: 'America/New_York',
    },
  ];

  const playersAppPermissionsOptions = [
    {
      key: 1,
      value: 'view_own_data',
      label: 'View Own Data',
    },
    {
      key: 2,
      value: 'view_no_data',
      label: 'View No Data',
    },
    {
      key: 3,
      value: 'view_all_data',
      label: 'View All Data',
    }
  ];

  const dateFormatOptions = [
    {
      key: 1,
      value: 'MM/DD/YYYY',
      label: 'MM/DD/YYYY',
    },
    {
      key: 2,
      value: 'DD/MM/YYYY',
      label: 'DD/MM/YYYY',
    },
    {
      key: 5,
      value: 'MM/DD/YY',
      label: 'MM/DD/YY',
    },
    {
      key: 6,
      value: 'YYYY/MM/DD',
      label: 'YYYY/MM/DD',
    },
    {
      key: 3,
      value: 'DD-MM-YYYY HH:MM:SS',
      label: 'DD-MM-YYYY HH:MM:SS',
    },
    {
      key: 4,
      value: 'MM/DD/YYYY HH:MM:SS pp',
      label: 'MM/DD/YYYY HH:MM:SS pp',
    },
    {
      key: 4,
      value: 'YYYY/MM/DDTHH:MM:SSZ',
      label: 'YYYY/MM/DDTHH:MM:SSZ',
    },
    {
      key: 7,
      value: 'int',
      label: 'Integer',
    }
  ];

  const handleColorChange = (pickerId, hexColorStr) => {
    // Check permission before allowing color changes
    if (!hasEditGeneralTeamSettingsPermission) {
      return;
    }
    
    const newTeamColors = {...teamColors};
    if (pickerId.startsWith("team-color-")) {
      const index = parseInt(pickerId.slice(-1)) - 1;
      newTeamColors["all"][index] = hexColorStr;

      if (index === 0) {
        newTeamColors["primary"] = hexColorStr;
        setSample1BGColor(hexColorStr);
      } else if (index === 1) {
        newTeamColors["secondary"] = hexColorStr;
        setSample2BGColor(hexColorStr);
      }
    } else if (pickerId.startsWith("font-color-")) {
      const index = parseInt(pickerId.slice(-1)) - 1;
      newTeamColors["font_colors"][index] = hexColorStr;

      if (index === 0) {
        setSample1TextColor(hexColorStr);
      } else if (index === 1) {
        setSample2TextColor(hexColorStr);
      }
    }
    dispatch(setModifiedSettings({teamColors: newTeamColors}))
  }

  // The backend rejects an acute constant that is not strictly below the chronic one.
  // Check on save, not on each keystroke, so a partly typed number does not raise a toast.
  const ewmaTimeConstantsAreValid = () => {
    if (!isEwma) {
      return true;
    }
    if (ewmaAcuteTimeConstant == null || ewmaChronicTimeConstant == null) {
      message.error("Enter both EWMA time constants");
      return false;
    }
    if (ewmaAcuteTimeConstant >= ewmaChronicTimeConstant) {
      message.error("EWMA acute time constant should be less than chronic time constant");
      return false;
    }
    return true;
  }

  const handleSave = async () => {
    if (!ewmaTimeConstantsAreValid()) {
      return;
    }
    const result = await dispatch(updateTeamSettings());
    if (result?.logoWasUploaded) {
      window.location.reload();
    }
  }

  const handleHomeTimezoneChange = (homeTimeZone) => {
    dispatch(setModifiedSettings({ homeTimeZone }));
  }

  const handleAutoOutlierApprovalChange = (automaticOutlierApproval) => {
    dispatch(setModifiedSettings({automaticOutlierApproval}));
  }

  const handleChronicIncludesAcuteChange = (chronicIncludesAcute) => {
    dispatch(setModifiedSettings({chronicIncludesAcute}));
  }

  const handlePlayersAppPermissionChange = (playersAppPermission) => {
    dispatch(setModifiedSettings({playersAppPermission}));
  }

  const handlePreferredDateFormatChange = (preferredDateFormat) => {
    dispatch(setModifiedSettings({preferredDateFormat}));
  }

  const handleTopSpeedPercThresholdChange = (topSpeedPercThreshold) => {
    dispatch(setModifiedSettings({topSpeedPercThreshold}));
  }

  const handleACWRAcuteWindowChange = (acwrAcuteWindow) => {
    if (acwrAcuteWindow > acwrChronicWindow) {
      message.error("ACWR acute window should be less than chronic window");
    } else {
      dispatch(setModifiedSettings({acwrAcuteWindow}));
    }
  }

  const handleACWRChronicWindowChange = (acwrChronicWindow) => {
    if (acwrChronicWindow < acwrAcuteWindow) {
      message.error("ACWR acute window should be less than chronic window");
    } else {
      dispatch(setModifiedSettings({acwrChronicWindow}));
    }
  }

  const handleACWRCalculationMethodChange = (acwrCalculationMethod) => {
    dispatch(setModifiedSettings({acwrCalculationMethod}));
  }

  const handleEwmaAcuteTimeConstantChange = (ewmaAcuteTimeConstant) => {
    dispatch(setModifiedSettings({ewmaAcuteTimeConstant}));
  }

  const handleEwmaChronicTimeConstantChange = (ewmaChronicTimeConstant) => {
    dispatch(setModifiedSettings({ewmaChronicTimeConstant}));
  }

  // Gate runs before the file reaches onChange — also catches drag-dropped files
  const beforeUploadLogo = (file) => {
    const isPng = file.type === 'image/png';
    if (!isPng) {
      message.error('Please upload a PNG image');
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB');
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto-upload; the file is handled in onChange
  };

  const handleLogoChange = (info) => {
    const file = info.file;
    if (file) {
      // Use FileReader to create a data URL (CSP-compliant, avoids blob: URL issues)
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      dispatch(setModifiedSettings({ teamLogo: file }));
    }
  };

  return (
    <div className="general-overview-settings">
      <PermissionedButton
        type="primary"
        onClick={handleSave}
        loading={loading}
        permissionCheck="EditGeneralTeamSettings"
      >
        Save Changes
      </PermissionedButton>
      <br></br>
      <Card className="settings-card team-colors-card">
        <div className="team-color-pickers">
          <div><strong>Team Colors</strong></div>
          <div className="picker-selection-info">
            <div className="team-color-picker-div">
              {
                teamColors && teamColors?.all && (
                  teamColors?.all.map((color, index) => {
                    const tertiaryIdx = (index - 2) + 1;
                    if (tertiaryIdx < 4) {
                      return (
                        <ColorPickerWithLabel 
                          key={`team-color-${index}`}
                          pickerId={`team-color-${index + 1}`}
                          handleColorChangeParent={handleColorChange}
                          defaultColor={color}
                          defaultText={
                            index === 0 ? "Primary" : (index === 1 ? "Secondary": `Tertiary ${tertiaryIdx}`)
                          }
                        />
                      )
                    } else {
                      return <></>
                    }
                  })
                )
              }
            </div>
          </div>
          <div className="font-colors-div">
            {
              teamColors && teamColors?.font_colors && (
                teamColors?.font_colors.map((color, index) => (
                  <ColorPickerWithLabel 
                    key={`font-color-${index}`}
                    pickerId={`font-color-${index + 1}`}
                    handleColorChangeParent={handleColorChange}
                    defaultColor={color}
                    defaultText={
                      index === 0 ? "Font 1" : (index === 1 ? "Font 2": null)
                    }
                    className="font-color-picker-div"
                  />
                ))
              )
            }
          </div>
          <div className="sample-text-div">
            <div 
              className="sample-text primary-text"
              style={{
                      color: sample1TextColor , 
                      backgroundColor: sample1BGColor,
                      borderColor: sample1BGColor
                    }}
            >
              Sample Text
            </div>
            <div 
              className="sample-text secondary-text"
              style={{
                      color: sample2TextColor, 
                      backgroundColor: sample2BGColor,
                      borderColor: sample2BGColor
                    }}
            >
              Sample Text
            </div>
            <div 
              className="sample-text secondary-text"
              style={{
                      color: '#d9534f', 
                      backgroundColor: sample2BGColor,
                      borderColor: sample2BGColor
                    }}
            >
              <b>Sample Text</b>
            </div>
            <div 
              className="sample-text secondary-text"
              style={{
                      color: '#FFC83D', 
                      backgroundColor: sample2BGColor,
                      borderColor: sample2BGColor
                    }}
            >
              <b>Sample Text</b>
            </div>
            <div 
              className="sample-text secondary-text"
              style={{
                      color: '#31B452', 
                      backgroundColor: sample2BGColor,
                      borderColor: sample2BGColor
                    }}
            >
              <b>Sample Text</b>
            </div>
          </div>
          <div className="desc-div">
            <Text 
              disabled
              className="description"
            >
              Choose your signature colors. These colors will be used in your custom reports.
            </Text>
          </div>
        </div>
      </Card>
      {isStaff && (
        <Card className="settings-card team-logo-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <strong>Team Logo</strong>
              <br />
              <br />
              <Text secondary className="description">
                Click to upload a new team logo. The watermark background helps verify your logo has<br />a transparent background. The logo will be saved without this watermark.
              </Text>
            </div>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 8, overflow: 'hidden', marginLeft: 24 }}>
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={beforeUploadLogo}
                onChange={handleLogoChange}
                accept=".png"
                style={{ width: 140, height: 140 }}
              >
                <div style={{ 
                  width: 140, 
                  height: 140, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundImage: 'url(https://gpsdv-marketing.s3.us-east-2.amazonaws.com/logo/gpsdv_logo.png)',
                  backgroundSize: '120px auto',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'space',
                  position: 'relative',
                  margin: -8
                }}>
                  {/* Semi-transparent overlay to make background logo pattern very faint */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(250, 250, 250, 0.8)',
                    zIndex: 0
                  }} />
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', position: 'relative', zIndex: 1 }}
                    />
                  ) : teamInfo?.team_logo ? (
                    <img 
                      src={teamInfo.team_logo} 
                      alt="Current Logo" 
                      style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', position: 'relative', zIndex: 1 }}
                    />
                  ) : (
                    <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                      <PlusOutlined style={{ fontSize: 24, color: '#999' }} />
                      <div style={{ marginTop: 8, color: '#999' }}>Upload Logo</div>
                    </div>
                  )}
                </div>
              </Upload>
            </div>
          </div>
        </Card>
      )}
      {/* <Card className="settings-card timezone-card">
        <div>
          <strong>Home Time zone</strong>
          <Select
            className="select-dd timezone-select"
            value={homeTimeZone}
            showSearch
            style={{
              width: 200,
            }}
            placeholder="Search to Select"
            optionFilterProp="label"
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
            }
            onChange={handleHomeTimezoneChange}
            options={timezoneOptions}
          />
        </div>
      </Card> */}
      <Card className="settings-card outlier-approval-card">
        <strong>Ask me before skipping outliers?</strong>
        <PermissionedSwitch 
          checked={automaticOutlierApproval}
          className="outlier-approval-switch"
          onChange={handleAutoOutlierApprovalChange}
          permissionCheck="EditGeneralTeamSettings"
        />
        <br></br>
        <br></br>
        <Text 
          disabled
          className="description"
        >
          <ul>
            <li>If enabled, you will be prompted to approve/reject detected outliers in the data during uploads.</li>
            <li>If disabled, our custom algorithm will automatically detect and disacrd outliers.</li>
          </ul>
        </Text>
      </Card>
      <Card className="settings-card app-permission-card">
        <div>
          <strong>Player's App Permissions</strong>
          <PermissionedSelect
            className="select-dd app-permission-select"
            showSearch
            style={{
              width: 200,
            }}
            placeholder="Search to Select"
            optionFilterProp="label"
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
            }
            value={playersAppPermission}
            options={playersAppPermissionsOptions}
            onChange={handlePlayersAppPermissionChange}
            permissionCheck="EditGeneralTeamSettings"
          />
        </div>
        <br></br>
        <Text 
          disabled
          className="description"
        >
          Select what level of data access you want to give your players on the Whistle app.
        </Text>
      </Card>
      {/* <Card className="settings-card date-format-card">
        <div>
          <strong>Source Date Format</strong>
          <Select
            className="select-dd date-format-select"
            showSearch
            style={{
              width: 250,
            }}
            placeholder="Search to Select"
            optionFilterProp="label"
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
            }
            value={preferredDateFormat}
            options={dateFormatOptions}
            onChange={handlePreferredDateFormatChange}
          />
        </div>
        <br></br>
        <Text 
          disabled
          className="description"
        >
          This is the format of the source data, either from an uploaded file or an API. 
        </Text>
      </Card> */}
      <Card className="settings-card">
        <ACWRThresholdConfig />
        <br></br>
        <Text
          disabled
          className="description"
        >
          These segments determine the color of an ACWR value. The lowest and the highest segment show that a player is underworked or overworked.
        </Text>
      </Card>
      <Card className="settings-card">
        <div className="acwr-window-card">
          <strong>ACWR Calculation</strong>
          <div className="acwr-method-div">
            <strong>Method</strong>
            <PermissionedSelect
              className="select-dd acwr-method-select"
              style={{
                width: 200,
              }}
              value={acwrCalculationMethod}
              options={acwrCalculationMethodOptions}
              onChange={handleACWRCalculationMethodChange}
              permissionCheck="EditGeneralTeamSettings"
            />
            {!isEwma && (
              <div className="chronic-includes-acute-div">
                <strong>Chronic Includes Acute?</strong>
                <PermissionedSwitch
                  checked={chronicIncludesAcute}
                  className="chronic-includes-acute-switch"
                  onChange={handleChronicIncludesAcuteChange}
                  permissionCheck="EditGeneralTeamSettings"
                />
              </div>
            )}
          </div>
          <div className="acute-chronic-windows">
            <div>
              <strong>Acute (days)</strong>
              <PermissionedInputNumber
                placeholder="Ex: 7"
                value={isEwma ? ewmaAcuteTimeConstant : acwrAcuteWindow}
                min={1}
                max={60}
                step={1}
                precision={0}
                className="ip acwr-limit-ip"
                onChange={isEwma ? handleEwmaAcuteTimeConstantChange : handleACWRAcuteWindowChange}
                permissionCheck="EditGeneralTeamSettings"
              />
            </div>
            <div className="chronic-window">
              <strong>Chronic (days)</strong>
              <PermissionedInputNumber
                placeholder={isEwma ? "Ex: 28" : "Ex: 21"}
                value={isEwma ? ewmaChronicTimeConstant : acwrChronicWindow}
                min={1}
                max={60}
                step={1}
                precision={0}
                className="ip acwr-limit-ip"
                onChange={isEwma ? handleEwmaChronicTimeConstantChange : handleACWRChronicWindowChange}
                permissionCheck="EditGeneralTeamSettings"
              />
            </div>
          </div>
        </div>
        <br></br>
        <Text 
          disabled
          className="description"
        >
          {isEwma
            ? "The EWMA method weights every past day of load, with the most recent days counting the most. The acute and chronic time constants set how fast each average forgets old load. A larger constant remembers longer. The ratio of the two averages is the ACWR."
            : "The Acute and Chronic windows are used to calculate the ACWR ratio for your metrics. These ratios are used to determine if a player is overworked or underworked."}
        </Text>
      </Card>
      {
        !hideTopSpeedThreshold && (
          <Card className="settings-card top-speed-threshold-card">
            <div>
              <strong>Top Speed Percent Threshold</strong>
              <PermissionedInputNumber 
                placeholder="Ex: 85" 
                value={topSpeedPercThreshold} 
                min={0}
                max={100}
                step={1}
                className="ip top-speed-threshold-ip"
                onChange={handleTopSpeedPercThresholdChange}
                permissionCheck="EditGeneralTeamSettings"
              />
            </div>
            <br></br>
            <Text 
              disabled
              className="description"
            >
              This threshold is used to provide custom insights on the platform and reports. 
            </Text>
          </Card>
        )
      }
      <br></br>
      <br></br>
      <PermissionedButton
        type="primary"
        onClick={handleSave}
        loading={loading}
        permissionCheck="EditGeneralTeamSettings"
      >
        Save Changes
      </PermissionedButton>
    </div>
  );
};
